import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'
import type { OperationVariables, TypedDocumentNode } from '@apollo/client'

import { isNotOfType, isOfType } from '~publish/helpers/typeGuards'

export type MutationError = {
  __typename: 'MutationError'
  message: string
}

const mutationError = (message: string): MutationError => ({
  __typename: 'MutationError',
  message,
})

type MutationResult = { __typename: string } | null | undefined

export type MutationErrorResult<
  TResult extends MutationResult,
  TSuccessTypename extends string,
> =
  | Exclude<NonNullable<TResult>, { __typename: TSuccessTypename }>
  | MutationError

type ReturnResult<
  TResult extends MutationResult,
  TSuccessTypename extends string,
> =
  | {
      success: true
      data: Extract<NonNullable<TResult>, { __typename: TSuccessTypename }>
      error: null
    }
  | {
      success: false
      data: null
      error: MutationErrorResult<TResult, TSuccessTypename>
    }

/**
 * This hook is a wrapper around `useMutation` that provides a more
 * convenient way to handle mutation results and errors for union types
 * in GraphQL responses.
 *
 * @example
 * ```ts
 * const [movePostToDrafts, { loading, data, error }] = useAction(
 *  MovePostToDrafts,
 *  // getter function for mutation result
 *  (data) => data.movePostToDraftsV2,
 *  {
 *    // name of the success type in the GraphQL response
 *    successTypename: 'PostActionSuccess',
 *    // ...options for useMutation, same as in the official docs
 *    refetchQueries: ['GetPostList'],
 *  }
 * )
 * ```
 */
export const useTypedMutation = <
  TSuccessTypename extends string,
  TMutationResult extends MutationResult,
  TData extends object,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: TypedDocumentNode<TData, TVariables>,
  getResult: (data: TData) => TMutationResult | null | undefined,
  options: useMutation.Options<NoInfer<TData>, NoInfer<TVariables>> & {
    successTypename: TSuccessTypename
  },
): readonly [
  (
    ...options: Parameters<ReturnType<typeof useMutation<TData, TVariables>>[0]>
  ) => Promise<ReturnResult<TMutationResult, TSuccessTypename>>,
  ReturnResult<TMutationResult, TSuccessTypename> & { loading: boolean },
] => {
  const { successTypename, ...mutationOptions } = options
  const [mutate, { loading, data, error }] = useMutation<TData, TVariables>(
    mutation,
    mutationOptions,
  )

  const action = useCallback(
    async (...mutateArgs: Parameters<typeof mutate>) => {
      try {
        const { data: mutationData, error: mutateError } = await mutate(
          ...mutateArgs,
        )
        const result = mutationData && getResult(mutationData)
        const isSuccess = isOfType(result, successTypename)

        return isSuccess
          ? {
              success: true as const,
              data: result,
              error: null,
            }
          : {
              success: false as const,
              error:
                result && isNotOfType(result, successTypename)
                  ? result
                  : mutationError(
                      mutateError ? mutateError.message : 'Unknown error',
                    ),
              data: null,
            }
      } catch (caughtError) {
        return {
          success: false as const,
          error: mutationError(
            caughtError instanceof Error
              ? caughtError.message
              : 'Unknown error',
          ),
          data: null,
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutate, successTypename],
  )

  const result = data && getResult(data)
  const isSuccess = isOfType(result, successTypename)

  const toReturn: ReturnResult<TMutationResult, TSuccessTypename> & {
    loading: boolean
  } = isSuccess
    ? {
        success: true as const,
        data: result,
        error: null,
        loading,
      }
    : {
        success: false as const,
        error:
          result && isNotOfType(result, successTypename)
            ? result
            : mutationError(error ? error.message : 'Unknown error'),
        data: null,
        loading,
      }

  return [action, toReturn]
}
